import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

/**
 * AuthCallback - Handles OAuth redirect and sends session to parent iframe
 */
const AuthCallback = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        logger.debug('🔄 Processing OAuth callback...');
        
        // First, try to get session from URL fragments
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          logger.error('❌ Session extraction error:', error);
          throw error;
        }

        if (data.session) {
          logger.debug('✅ Auth callback successful, session received:', {
            hasAccessToken: !!data.session.access_token,
            userId: data.session.user?.id,
            email: data.session.user?.email
          });
          
          // Send session to parent iframe if exists (Preview Mode)
          if (window.opener) {
            logger.debug('📤 Sending session to parent window...', {
              popupOrigin: window.location.origin,
              hasOpener: !!window.opener
            });
            
            try {
              // Send to parent window - use '*' to allow cross-origin iframe communication
              window.opener.postMessage({
                type: 'SUPABASE_AUTH_TOKEN',
                session: data.session,
                timestamp: Date.now()
              }, '*');
              
              logger.debug('✅ Session sent to parent successfully');
              
              // Cleanup any window-specific auth handlers
              const windowName = window.name;
              if (windowName && window.opener[`_authCleanup_${windowName}`]) {
                window.opener[`_authCleanup_${windowName}`]();
                delete window.opener[`_authCleanup_${windowName}`];
              }
            } catch (e) {
              logger.error('❌ Failed to send session to parent:', e);
            }
            
            // Close popup with slight delay for message delivery
            setTimeout(() => {
              logger.debug('🚪 Closing popup window...');
              window.close();
            }, 500);
            
            return;
          }
          
          // Normal redirect if not in popup
          logger.debug('🔄 Normal OAuth flow - redirecting to home');
          navigate('/', { replace: true });
        } else {
          logger.error('❌ No session received from callback');
          throw new Error('No session received');
        }
      } catch (error) {
        logger.error('❌ Auth callback error:', error);
        
        // Send error to parent if in popup
        if (window.opener) {
          logger.debug('📤 Sending error to parent window...');
          
          try {
            window.opener.postMessage({
              type: 'SUPABASE_AUTH_TOKEN',
              error: error instanceof Error ? error.message : 'Authentication failed',
              timestamp: Date.now()
            }, '*');
            
            logger.debug('✅ Error sent to parent successfully');
          } catch (e) {
            logger.error('❌ Failed to send error to parent:', e);
          }
          
          setTimeout(() => {
            window.close();
          }, 500);
          
          return;
        }
        
        // Redirect to login with error
        navigate('/auth?error=callback_failed', { replace: true });
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Đang xử lý đăng nhập...</p>
      </div>
    </div>
  );
};

export default AuthCallback;